import {
  type WorkflowsEdge,
  type WorkflowsEdgeInput,
  type WorkflowsNode,
  type WorkflowsNodeInput,
  WorkflowsValueObjectsNodeTypeEnum,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  EdgeUI,
  type NodeUI,
  NodeUIApproval,
  NodeUICondition,
  NodeUITask,
  NodeUITrigger,
  NodeUIType,
  isValidActionGroup,
} from 'modules/workflows/components/Node/types'
import { deserializeWorkflowNodes, serializeNodeData } from 'modules/workflows/lib/backend-adapter'
import {
  BackendActionNode,
  BackendActionNodeData,
  BackendNode,
  BackendNodeData,
  BackendStartrNode,
  BackendSwitchComparison,
  BackendSwitchNode,
  BackendSwitchNodeData,
  isBackendActionNode,
  isBackendStartNode,
  isBackendSwitchNode,
  isBackendTerminateNode,
} from 'modules/workflows/lib/backend-types'
import {
  branchTransitionEdgeId,
  conditionParentEdgeId,
  conditionSplitEdgeId,
  conditionBranchId as makeConditionBranchId,
  normalEdgeId,
} from 'modules/workflows/lib/flow/ids'

const mapToNodeUI = (
  node: BackendNode,
  handlers: Array<{ name: string; actionGroup: string }>
): NodeUI | NodeUI[] => {
  // ignore terminate node, we don't support them on UI for now
  if (isBackendTerminateNode(node)) {
    return []
  }

  if (isBackendSwitchNode(node)) {
    return mapSwitchNode(node)
  }

  if (isBackendActionNode(node)) {
    return mapActionNode(node, handlers)
  }

  if (isBackendStartNode(node)) {
    return mapStartNode(node)
  }

  throw new Error(`Unknown node type: ${node}`)
}

const mapActionNode = (
  node: BackendActionNode,
  handlers: Array<{ name: string; actionGroup: string }>
): NodeUITask | NodeUIApproval => {
  const handler = handlers.find((h) => h.name === node.data.handler_name)

  if (!handler) {
    throw new Error(
      `Could not find handler: ${node.data.handler_name}. Available handlers: ${handlers
        .map((h) => h.name)
        .join(', ')}`
    )
  }

  if (!isValidActionGroup(handler.actionGroup)) {
    throw new Error(
      `Invalid action_group '${handler.actionGroup}' for handler: ${node.data.handler_name}. Expected 'task' or 'approvals'.`
    )
  }

  const action_group = handler.actionGroup

  return {
    id: node.identifier,
    position: { x: 0, y: 0 },
    data: {
      step: 1,
      title: node.title || i18n.t('workflows.node_type.task.default.title'),
      description: node.description || i18n.t('workflows.node_type.task.default.description'),
      isValid: true,
      payload: node.data.payload,
      handler_name: node.data.handler_name,
      action_group: action_group,
    },
    type: action_group,
  }
}

const mapStartNode = (node: BackendStartrNode): NodeUITrigger => {
  return {
    id: node.identifier,
    position: { x: 0, y: 0 },
    data: {
      step: 1,
      title: node.title || i18n.t('workflows.node_type.trigger.default.title'),
      description: node.description || i18n.t('workflows.node_type.trigger.default.description'),
      isValid: true,
      ...node.data,
    },
    type: 'trigger',
  }
}

const createConditionIfNodes = (
  parentNodeId: string,
  comparisons: BackendSwitchComparison[]
): NodeUI[] => {
  return comparisons.map((comparison) => ({
    id: makeConditionBranchId(parentNodeId, `if-${comparison.label}`),
    position: { x: 0, y: 0 },
    data: {
      step: 1,
      title: i18n.t('workflows.node_type.condition_if.title', { label: comparison.label }),
      description: i18n.t('workflows.node_type.condition_if.description'),
      isValid: true,
      comparison,
    },
    type: 'conditionIf' as const,
  }))
}

const createConditionSplitEdges = (
  parentNodeId: string,
  comparisons: BackendSwitchComparison[]
): EdgeUI[] => {
  return comparisons.map((comparison) => ({
    id: conditionSplitEdgeId(parentNodeId, comparison.label),
    source: parentNodeId,
    target: makeConditionBranchId(parentNodeId, `if-${comparison.label}`),
    label: comparison.label,
  }))
}

export const createAllConditionChildNodes = (
  parentNodeId: string,
  comparisons: BackendSwitchComparison[]
): NodeUI[] => {
  const conditionIfNodes = createConditionIfNodes(parentNodeId, comparisons)

  const elseNode: NodeUI = {
    id: makeConditionBranchId(parentNodeId, 'else'),
    position: { x: 0, y: 0 },
    data: {
      step: 1,
      title: i18n.t('workflows.node_type.condition_else.title'),
      description: i18n.t('workflows.node_type.condition_else.description'),
      isValid: true,
    },
    type: 'conditionElse',
  }

  return [...conditionIfNodes, elseNode]
}

export const createAllConditionChildEdges = (
  parentNodeId: string,
  comparisons: BackendSwitchComparison[]
): EdgeUI[] => {
  const conditionIfEdges = createConditionSplitEdges(parentNodeId, comparisons)

  const elseEdge: EdgeUI = {
    id: conditionSplitEdgeId(parentNodeId, 'false'),
    source: parentNodeId,
    target: makeConditionBranchId(parentNodeId, 'else'),
    label: 'default',
  }

  return [...conditionIfEdges, elseEdge]
}

const mapSwitchNode = (node: BackendSwitchNode): NodeUI[] => {
  return [
    {
      id: node.identifier,
      position: { x: 0, y: 0 },
      data: {
        step: 1,
        title: node.title || i18n.t('workflows.node_type.condition.default.title'),
        description:
          node.description || i18n.t('workflows.node_type.condition.default.description'),
        isValid: true,
        comparisons: node.data.comparisons,
      },
      type: 'condition' as const,
    },
    ...createAllConditionChildNodes(node.identifier, node.data.comparisons),
  ]
}

const mapToFlowEdgesForNode = (node: BackendNode): EdgeUI[] => {
  if (!isBackendSwitchNode(node)) return []

  return createAllConditionChildEdges(node.identifier, node.data.comparisons)
}

const mapToFlowEdgesForEdge = (
  edge: WorkflowsEdge,
  getNode: (id: string) => BackendNode | undefined
): EdgeUI[] => {
  const fromId = String(edge.fromNodeIdentifier)
  const toId = String(edge.toNodeIdentifier)
  const from = getNode(fromId)
  const to = getNode(toId)
  if (!from || !to) return []

  if (isBackendSwitchNode(from)) {
    // Try to match the edge label with a comparison label
    const comparison = from.data.comparisons.find((comp) => comp.label === edge.label)
    const branchId = comparison
      ? makeConditionBranchId(from.identifier, `if-${comparison.label}`)
      : makeConditionBranchId(from.identifier, 'else')

    return [
      {
        id: branchTransitionEdgeId(fromId, comparison ? 'true' : 'false', toId),
        source: branchId,
        target: toId,
        label: edge.label ?? undefined,
      },
    ]
  }

  if (to.nodeType === 'switch') {
    return [
      {
        id: conditionParentEdgeId(fromId, toId),
        source: fromId,
        target: toId,
      },
    ]
  }

  return [
    {
      id: normalEdgeId(fromId, toId),
      source: fromId,
      target: toId,
      label: edge.label ?? undefined,
    },
  ]
}

// Transform GraphQL server data to UI state
export const mapGraphQLToUI = (
  workflowNodes: WorkflowsNode[],
  workflowEdges: WorkflowsEdge[],
  handlers: Array<{ name: string; actionGroup: string }>,
  nodeAttachmentBuilder?: (node: NodeUI) => React.ReactNode | undefined
): { nodesUI: NodeUI[]; edgesUI: EdgeUI[] } => {
  const backendNodes = deserializeWorkflowNodes(workflowNodes)

  const nodesById = Object.fromEntries(backendNodes.map((n) => [String(n.identifier), n]))

  const nodesUI = backendNodes
    .map((node) => mapToNodeUI(node, handlers))
    .flat()
    .map((n) => {
      const updatedNode = { ...n, position: { x: 0, y: 0 } }
      updatedNode.data = {
        ...n.data,
        nodeAttachment: nodeAttachmentBuilder
          ? nodeAttachmentBuilder({ ...n, selected: false })
          : undefined,
      }
      return updatedNode
    })

  const structural = backendNodes.flatMap((n) => mapToFlowEdgesForNode(n))
  const transitions = workflowEdges.flatMap((e) =>
    mapToFlowEdgesForEdge(e, (id: string) => nodesById[id])
  )
  const edgesUI = [...structural, ...transitions]

  return { nodesUI, edgesUI }
}

// Helper function to check if a node is a condition child (should not be sent to backend)
const isConditionUIChild = (node: NodeUI): boolean => {
  return node.type === 'conditionIf' || node.type === 'conditionElse'
}

// Type guards for NodeUI types
const isNodeUITrigger = (node: NodeUI): node is NodeUITrigger => {
  return node.type === 'trigger'
}

const isNodeUICondition = (node: NodeUI): node is NodeUICondition => {
  return node.type === 'condition'
}

const isNodeUIAction = (node: NodeUI): node is NodeUITask | NodeUIApproval => {
  return node.type === 'task' || node.type === 'approvals'
}

// Helper function to extract backend data from NodeUI
const extractBackendData = (node: NodeUI): BackendNodeData => {
  if (isNodeUITrigger(node)) {
    return {} // BackendStartNodeData is an empty object
  }

  if (isNodeUICondition(node)) {
    return {
      _type: 'switch_data',
      comparisons: node.data.comparisons || [],
    } satisfies BackendSwitchNodeData
  }

  if (isNodeUIAction(node)) {
    return {
      _type: 'action_data',
      payload: node.data.payload || {},
      handler_name: node.data.handler_name || '',
    } satisfies BackendActionNodeData
  }

  throw new Error(`Cannot extract backend data from unknown UI node type: ${node.type}`)
}

// Helper function to map UI node types to backend node types
const mapUITypeToBackendType = (uiType: NodeUIType): WorkflowsValueObjectsNodeTypeEnum => {
  switch (uiType) {
    case 'trigger':
      return 'start'
    case 'condition':
      return 'switch'
    case 'task':
    case 'approvals':
      return 'action'
    default:
      throw new Error(`Unknown UI node type: ${uiType}`)
  }
}

// Serialization functions for mutations
export const serializeNodesForMutation = (nodes: NodeUI[]): WorkflowsNodeInput[] => {
  return nodes
    .filter((node) => !isConditionUIChild(node)) // Exclude condition children
    .map((node) => {
      const backendData = extractBackendData(node)
      return {
        identifier: node.id,
        nodeType: mapUITypeToBackendType(node.type),
        data: serializeNodeData(backendData),
        title: node.data.title || null,
        description: node.data.description || null,
      }
    })
}

export const serializeEdgesForMutation = (
  edges: EdgeUI[],
  nodes: NodeUI[]
): WorkflowsEdgeInput[] => {
  const nodeMap = new Map(nodes.map((node) => [node.id, node]))

  // Pre-compute outoging edges of condition children by source
  const childEdgesBySource = edges
    .filter((edge) => {
      const sourceNode = nodeMap.get(edge.source)
      return sourceNode && isConditionUIChild(sourceNode)
    })
    .reduce((acc, edge) => {
      const existing = acc.get(edge.source) || []
      //accumulate edges of each children by source
      acc.set(edge.source, [...existing, edge])
      return acc
    }, new Map<string, EdgeUI[]>())

  // Map edges to backend edges
  return edges.flatMap((edge) => {
    const source = nodeMap.get(edge.source)
    const target = nodeMap.get(edge.target)

    // Skip internal structure edges (condition → condition children)
    // but convert them to workflow transitions by following the child's outgoing edges
    if (source && isNodeUICondition(source) && target && isConditionUIChild(target)) {
      const childEdges = childEdgesBySource.get(edge.target) || []
      return childEdges.map((childEdge) => ({
        fromIdentifier: edge.source, // parent condition
        toIdentifier: childEdge.target, // final target
        label: edge.label?.toString() ?? null,
      }))
    }

    // Skip edges from condition children (they're handled above)
    if (source && isConditionUIChild(source)) {
      return []
    }

    // Keep all other edges as-is
    return [
      {
        fromIdentifier: edge.source,
        toIdentifier: edge.target,
        label: edge.label?.toString() ?? null,
      },
    ]
  })
}
